<template>
  <modal :show="show" @close="$emit('close')">
    <template v-slot:header>
      <h5>{{ isBatch ? `Assign ${batchMacs.length} Devices` : (mode === 'add' ? 'Add New Device' : 'Edit Device') }}</h5>
    </template>

    <!-- MAC Address (hidden for batch, shown for single) -->
    <b-form-group v-if="!isBatch" label="MAC Address *">
      <b-form-input v-model="form.mac" :disabled="mode === 'edit'" />
    </b-form-group>

    <!-- Location & Status side by side -->
    <b-row>
      <b-col cols="7">
        <b-form-group label="Current Location">
          <b-form-select v-model="form.currentLocation" size="sm">
            <option :value="null" disabled>Select location or create new...</option>
            <option value="__new__">-- Create New --</option>
            <option v-for="c in allCompanies" :key="c" :value="c">{{ c }}</option>
          </b-form-select>
          <b-input-group v-if="form.currentLocation === '__new__'" size="sm" class="mt-1">
            <b-form-input v-model="newCompanyName" placeholder="Company name..." />
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="5">
        <b-form-group label="Status">
          <b-form-select v-model="form.currentStatus" :options="statusOptions" size="sm" />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Duration (only when Status is Test) -->
    <b-row v-if="form.currentStatus === 'Test'">
      <b-col cols="6">
        <b-form-group label="Duration (days)">
          <b-form-input v-model.number="form.durationDays" type="number" min="1" size="sm" placeholder="e.g., 30" />
        </b-form-group>
      </b-col>
      <b-col cols="6" class="d-flex align-items-end pb-2">
        <small class="text-muted">Expected return: {{ computedDueDate || '—' }}</small>
      </b-col>
    </b-row>

    <!-- Batch -->
    <b-form-group label="Batch">
      <b-form-select v-model="form.batch" size="sm">
        <option :value="null" disabled>Select batch or create new...</option>
        <option value="__new__">-- Create New Batch --</option>
        <option v-for="b in batchOptions" :key="b.value" :value="b.value">{{ b.text }}</option>
      </b-form-select>
      <b-row v-if="form.batch === '__new__'" class="mt-1">
        <b-col cols="4"><b-form-input v-model="newBatch.number" placeholder="Number" size="sm" /></b-col>
        <b-col cols="4"><b-form-input v-model="newBatch.characteristics" placeholder="Chars" size="sm" /></b-col>
        <b-col cols="4"><b-form-input v-model="newBatch.deliveryDate" type="date" size="sm" /></b-col>
      </b-row>
    </b-form-group>


    <!-- Owner / Distributor -->
    <b-form-group label="Owner / Distributor">
      <b-form-select v-model="form.owner" size="sm">
        <option :value="null">— None —</option>
        <option value="Infrafon">Infrafon</option>
        <option value="InTech">InTech</option>
        <option value="__custom__">-- Other --</option>
      </b-form-select>
      <b-input-group v-if="form.owner === '__custom__'" size="sm" class="mt-1">
        <b-form-input v-model="customOwner" placeholder="Owner name..." />
      </b-input-group>
    </b-form-group>

    <!-- Quality Issues -->
    <b-form-group label="Quality Issues">
      <b-dropdown text="Select issues" block variant="outline-secondary" size="sm">
        <div class="px-3 py-2">
          <b-form-checkbox-group v-model="form.qualityIssuesArray" :options="qualityIssueOptions" stacked />
          <b-form-input v-model="form.customQualityIssue" placeholder="Custom issue…" size="sm" class="mt-2" />
        </div>
      </b-dropdown>
      <div v-if="form.qualityIssuesArray.length || form.customQualityIssue" class="mt-1">
        <b-badge v-for="issue in form.qualityIssuesArray" :key="issue" variant="danger" class="mr-1">{{ issue }}</b-badge>
        <b-badge v-if="form.customQualityIssue" variant="secondary" class="mr-1">{{ form.customQualityIssue }}</b-badge>
      </div>
    </b-form-group>

    <!-- Notes -->
    <b-form-group label="Notes">
      <b-form-textarea v-model="form.notes" rows="2" placeholder="Notes..." size="sm" />
    </b-form-group>

    <template v-slot:footer>
      <base-button type="outline-secondary" @click="$emit('close')">Cancel</base-button>
      <base-button type="primary" :disabled="!isBatch && !form.mac.trim()" @click="save">
        {{ isBatch ? `Save ${batchMacs.length} Devices` : 'Save' }}
      </base-button>
    </template>
  </modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { statusOptionsList, commonQualityIssues } from '@/utils/statusHelpers'

export default {
  name: 'DeviceFormModal',
  props: {
    show: { type: Boolean, required: true },
    mode: { type: String, default: 'add' },
    initialMac: { type: String, default: '' },
    device: { type: Object, default: null },
    batchMacs: { type: Array, default: () => [] }   // ← new
  },
  data() {
    return {
      form: {
        mac: '',
        currentLocation: null,
        currentStatus: 'OK',
        batch: null,
         owner: null, 
        qualityIssuesArray: [],
        customQualityIssue: '',
        notes: '',
        durationDays: 30
      },
      customOwner: '', 
      newCompanyName: '',
      newBatch: {
        number: '',
        characteristics: '',
        deliveryDate: ''
      }
    }
  },
  computed: {
    ...mapState('inventory', ['companies', 'batches']),
    isBatch() { return this.batchMacs.length > 0 },
    allCompanies() { return this.companies },
    statusOptions() { return statusOptionsList.map(s => ({ value: s, text: s })) },
    batchOptions() {
      return this.batches.map(b => ({
        value: `Batch ${b.number} (${b.deliveryDate})`,
        text: `Batch ${b.number} (${b.deliveryDate})`
      }))
    },
    qualityIssueOptions() { return commonQualityIssues.map(s => ({ value: s, text: s })) },
    computedDueDate() {
      if (this.form.currentStatus !== 'Test' || !this.form.durationDays) return ''
      const d = new Date()
      d.setDate(d.getDate() + this.form.durationDays)
      return d.toISOString().split('T')[0]
    }
  },
  watch: {
    show(val) { if (val) this.resetForm() }
  },
  methods: {
    ...mapActions('inventory', ['addDevice', 'updateDevice', 'addCompany', 'addBatch']),

    resetForm() {
      if (!this.isBatch && this.mode === 'edit' && this.device) {
        this.form.mac = this.device.mac
        this.form.currentLocation = this.device.currentLocation
        this.form.currentStatus = this.device.currentStatus
        const issues = (this.device.qualityIssues || '').split(/[,.;] +/).map(i => i.trim()).filter(Boolean)
        this.form.qualityIssuesArray = issues.filter(i => commonQualityIssues.includes(i))
        this.form.customQualityIssue = issues.filter(i => !commonQualityIssues.includes(i)).join(', ')
        this.form.notes = this.device.notes || ''
        this.form.batch = this.device.batch || null
        this.form.durationDays = 30
        this.form.owner = this.device.owner || null
        this.customOwner = ''
      } else {
        this.form = {
          mac: this.isBatch ? '' : (this.initialMac || ''),
          currentLocation: null,
          currentStatus: 'OK',
          batch: null,
          qualityIssuesArray: [],
          customQualityIssue: '',
          notes: '',
          durationDays: 30
        }
      }
      this.newCompanyName = ''
      this.newBatch = { number: '', characteristics: '', deliveryDate: '' }
    },

    async save() {
      // Resolve location
      let finalLocation = this.form.currentLocation
      if (finalLocation === '__new__') {
        const name = this.newCompanyName.trim()
        if (!name) { alert('Please enter a company name.'); return }
        if (this.allCompanies.includes(name)) { alert('Company already exists.'); return }
        await this.addCompany(name)
        finalLocation = name
      }

      // Resolve batch
      let finalBatch = this.form.batch
      if (finalBatch === '__new__') {
        const { number, characteristics, deliveryDate } = this.newBatch
        if (!number || !characteristics || !deliveryDate) {
          alert('Please fill in all batch fields.'); return
        }
        if (this.batches.some(b => b.number === number)) {
          alert('Batch number already exists.'); return
        }
        await this.addBatch({ number, quantity: 0, characteristics, deliveryDate, history: '' })
        finalBatch = `Batch ${number} (${deliveryDate})`
      }

      // Combine quality issues
      const allIssues = [
        ...this.form.qualityIssuesArray,
        ...(this.form.customQualityIssue ? this.form.customQualityIssue.split(/[,;]+/).map(s => s.trim()).filter(Boolean) : [])
      ]
      const qualityStr = allIssues.join(', ')

      // Calculate dueDate
      let dueDate = null
      if (this.form.currentStatus === 'Test' && this.form.durationDays) {
        const d = new Date()
        d.setDate(d.getDate() + this.form.durationDays)
        dueDate = d.toISOString().split('T')[0]
      }
      const finalOwner = this.form.owner === '__custom__'
  ? this.customOwner.trim()
  : this.form.owner

      const deviceData = {
        currentLocation: finalLocation || 'InTech stock',
        currentStatus: this.form.currentStatus,
        batch: finalBatch || 'Unknown',
        owner: finalOwner || '', 
        qualityIssues: qualityStr,
        notes: this.form.notes,
        dueDate: dueDate
      }

      if (this.isBatch) {
        // Create one device per MAC in batch list
        for (const mac of this.batchMacs) {
          const exists = this.devices ? this.devices.some(d => d.mac === mac) : false
          if (!exists) {
            await this.addDevice({ mac, ...deviceData })
          }
        }
        alert(`${this.batchMacs.length} devices added.`)
      } else {
        // Single device
        const exists = this.devices ? this.devices.some(d => d.mac === this.form.mac) : false
        if (exists) {
          alert('Device already exists.')
          return
        }
        if (this.mode === 'add') {
          await this.addDevice({ mac: this.form.mac.trim(), ...deviceData })
        } else {
          await this.updateDevice({ ...this.device, ...deviceData })
        }
      }

      this.$emit('saved')
      this.$emit('close')
    }
  }
}
</script>